import { festivalWindow, hasTime } from './dates'

// The savePregame callable's contract, shared so the form and the server
// validate the same way.

export const PREGAME_PLACE_NAME_MAX = 80
export const PREGAME_ADDRESS_MAX = 200
export const SAVED_PLACES_MAX = 10
export const PREGAME_NOTES_MAX = 500
export const PREGAME_AUDIENCE_GROUPS_MAX = 50
export const PREGAME_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

export interface PregameFields {
  day?: string
  time?: string
  placeName?: string
  address?: string
  notes?: string
}

/**
 * users/{uid}/private/places — the places a host has used for pre-games, most
 * recent first, so the next one can start from them. savePregame adds to it;
 * the owner may remove entries.
 */
export interface SavedPlace {
  name?: string
  address?: string
  lastUsedAt: number
}

export interface SavedPlacesDoc {
  places: SavedPlace[]
}

/** Same place regardless of case/spacing, so re-using one doesn't duplicate it. */
export function savedPlaceKey(p: { name?: string; address?: string }): string {
  const norm = (s?: string) => (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ')
  return `${norm(p.name)}|${norm(p.address)}`
}

/** Both absent = everyone: all the host's groups plus friends. */
export interface PregameAudience {
  audienceGroupIds?: string[]
  audienceFriends?: boolean
}

export interface SavePregameRequest extends PregameFields, PregameAudience {
  eventId: string
}

export interface SavePregameResult {
  /** False when the request had no content and any existing pre-game was removed. */
  saved: boolean
  created: boolean
}

/** Who's going: the host plus the invitees who said yes. */
export function pregameGoingIds(p: { hostId: string; goingIds?: string[] }): string[] {
  return [p.hostId, ...(p.goingIds ?? []).filter(id => id !== p.hostId)]
}

export function hasPregameContent(p: Partial<PregameFields> | undefined): boolean {
  return !!(p?.time || p?.placeName?.trim() || p?.address?.trim() || p?.notes?.trim())
}

/**
 * Tickets and pre-games are party features. A festival crew buys passes months
 * ahead and meets up inside (map pins, check-ins), so on a festival both only
 * confused (#661); old festival docs stay but are neither shown nor writable.
 */
export function offersTicketsAndPregames(event: { type: string }): boolean {
  return event.type !== 'festival'
}

export function isRestrictedAudience(a: PregameAudience): boolean {
  return (a.audienceGroupIds?.length ?? 0) > 0 || a.audienceFriends === true
}

/** The days a festival pre-game can be on; empty for a party (its date is implied). */
export function pregameDays(event: {
  type: string
  startDate: string
  endDate?: string | null
  firstSetDay?: string
  lastSetDay?: string
}): string[] {
  if (event.type !== 'festival') return []
  const { startDate, endDate } = festivalWindow(event)
  const first = startDate.slice(0, 10)
  const last = (endDate ?? startDate).slice(0, 10)
  const days: string[] = []
  const d = new Date(`${first}T00:00:00Z`)
  while (days.length < 31) {
    const iso = d.toISOString().slice(0, 10)
    if (iso > last) break
    days.push(iso)
    d.setUTCDate(d.getUTCDate() + 1)
  }
  return days
}

/**
 * A new pre-game's suggested time: an hour before the event starts, when its
 * start has a time. On a festival only for its first day — later days' start
 * times aren't known.
 */
export function defaultPregameTime(event: { startDate: string }, day?: string): string {
  if (!hasTime(event.startDate)) return ''
  if (day && day !== event.startDate.slice(0, 10)) return ''
  const [h, m] = event.startDate.slice(11, 16).split(':').map(Number)
  const minutes = (h * 60 + m - 60 + 24 * 60) % (24 * 60)
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
}
